import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AptitudeSection.css';

const AptitudeSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('quant');
  const [activeTopicIndex, setActiveTopicIndex] = useState(0);

  const categories = {
    quant: {
      title: 'Quantitative Aptitude',
      icon: '📊',
      badgeColor: 'blue-glow',
      topics: [
        { name: 'Time & Work' },
        { name: 'Ratio & Proportion' },
        { name: 'Profit & Loss' },
        { name: 'Permutations & Combinations' },
        { name: 'Probability' },
        { name: 'Simple & Compound Interest' },
        { name: 'Time, Speed & Distance' },
        { name: 'Averages & Alligations' }
      ]
    },
    logical: {
      title: 'Logical Reasoning',
      icon: '🧩',
      badgeColor: 'purple-glow',
      topics: [
        { name: 'Blood Relations' },
        { name: 'Coding-Decoding' },
        { name: 'Seating Arrangements' },
        { name: 'Number & Letter Series' },
        { name: 'Syllogisms' },
        { name: 'Direction Sense' },
        { name: 'Clocks & Calendars' }
      ]
    },
    verbal: {
      title: 'Verbal Ability',
      icon: '✍️',
      badgeColor: 'orange-glow',
      topics: [
        { name: 'Sentence Correction' },
        { name: 'Reading Comprehension' },
        { name: 'Synonyms & Antonyms' },
        { name: 'Common Grammar Errors' },
        { name: 'Idioms & Phrases' }
      ]
    }
  };

  const currentCategory = categories[selectedCategory];
  const activeTopic = currentCategory.topics[activeTopicIndex] || currentCategory.topics[0];

  const handleCategoryChange = (key) => {
    setSelectedCategory(key);
    setActiveTopicIndex(0);
  };

  return (
    <section className="ucp-section aptitude-mastery-section">
      <div className="aptitude-container">
        <div className="aptitude-content">

          {/* Left Side: Interactive Mock Player and Dashboard */}
          <motion.div
            className="aptitude-visual"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="dashboard-frame">
              {/* Window Controls */}
              <div className="dashboard-header">
                <div className="window-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <div className="dashboard-title">Aptitude Learning Portal</div>
              </div>

              {/* Video Mock Player */}
              <div className="mock-video-player">
                <div className="player-inner">
                  {/* Subtle Grid overlay */}
                  <div className="grid-overlay"></div>

                  {/* Glowing core indicator */}
                  <div className="player-glow"></div>

                  <div className="player-overlay-content">
                    <span className="live-pill">VIDEO CONTENT</span>
                    <div className="lock-icon-container">
                      <div className="lock-ripple"></div>
                      <span className="lock-emoji">🔒</span>
                    </div>
                    <h4 className="player-topic-title">{activeTopic.name}</h4>
                    <p className="player-hint">Full HD Access provided upon enrollment</p>
                  </div>

                  {/* Player Controls Bar */}
                  <div className="player-controls">
                    <div className="play-btn-circle">
                      <div className="play-triangle"></div>
                    </div>
                    <div className="timeline-bar">
                      <div className="timeline-fill" style={{ width: '40%' }}></div>
                      <div className="timeline-knob" style={{ left: '40%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Topics List Inside Dashboard */}
              <div className="dashboard-topics-list">
                <div className="list-header">
                  <span>Selected Lectures ({currentCategory.topics.length})</span>
                  <span className="total-badge">300+ Questions with Video Explanations</span>
                </div>
                <div className="lectures-scrollable">
                  {currentCategory.topics.map((topic, index) => (
                    <div
                      key={topic.name}
                      className={`lecture-row ${activeTopicIndex === index ? 'active' : ''}`}
                      onClick={() => setActiveTopicIndex(index)}
                    >
                      <div className="play-status-indicator">
                        {activeTopicIndex === index ? (
                          <div className="wave-animation">
                            <span></span><span></span><span></span>
                          </div>
                        ) : (
                          <span className="play-small">▶</span>
                        )}
                      </div>
                      <div className="lecture-row-details">
                        <span className="topic-name">{topic.name}</span>
                      </div>
                      <span className="lock-badge-small">🔒</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side: Text Information and Interactive Tabs */}
          <motion.div
            className="aptitude-info"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="aptitude-badge">APTITUDE PREPARATION</span>

            <h2 className="aptitude-title">
              Crack Elimination Rounds: <br />
              <span className="blue-text">300+ Aptitude Questions</span>
            </h2>

            <p className="aptitude-desc">
              Breeze through the initial hiring assessments of major IT giants. We cover <strong>34 critical topics</strong> across Quantitative, Logical Reasoning, and Verbal sections with premium <strong>video access</strong> and step-by-step explanations.
            </p>

            {/* Interactive Category Tabs */}
            <div className="category-tabs">
              {Object.keys(categories).map((key) => (
                <button
                  key={key}
                  className={`tab-btn ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(key)}
                >
                  <span className="tab-icon">{categories[key].icon}</span>
                  <span className="tab-text">{categories[key].title}</span>
                </button>
              ))}
            </div>

            {/* Quick stats grid */}
            <div className="aptitude-stats-grid">
              <div className="ap-stat-card">
                <strong>34+</strong>
                <span>Core Topics</span>
              </div>
              <div className="ap-stat-card">
                <strong>300+</strong>
                <span>Practice Questions</span>
              </div>
              <div className="ap-stat-card">
                <strong>100%</strong>
                <span>Video Solutions</span>
              </div>
            </div>

            {/* Feature bullets */}
            <ul className="aptitude-features-list">
              <li>
                <span className="feat-check">✓</span>
                <div>
                  <strong>Complete Video Explanations</strong>
                  <span>Every question is solved step-by-step to build speed and accuracy.</span>
                </div>
              </li>
              <li>
                <span className="feat-check">✓</span>
                <div>
                  <strong>Company-Wise Shortcuts & Tricks</strong>
                  <span>Special sessions focused on time-saving techniques for coding rounds.</span>
                </div>
              </li>
            </ul>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AptitudeSection;
